import { Button, type Input, quantizePitch, quantizeStick, quantizeYaw } from '@battle/sim';

/**
 * Keyboard + mouse (Pointer Lock) and Gamepad API → one sim `Input` per tick (docs/02 §2).
 * Look input is returned separately as deltas; the camera owns yaw/pitch and hands them back
 * when sampling, so the sim always moves relative to what the player sees.
 */
export type Action =
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'jump'
  | 'dodge'
  | 'brace'
  | 'attack'
  | 'aim'
  | 'lockOn';

const DEFAULT_KEYS: Record<Action, string[]> = {
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space'],
  dodge: ['ShiftLeft', 'ShiftRight'],
  brace: ['KeyC'],
  attack: ['Mouse0'],
  aim: ['Mouse2'],
  lockOn: ['Tab', 'Mouse1'],
};

// Standard gamepad mapping (Xbox layout).
const PAD = { A: 0, B: 1, X: 2, Y: 3, LB: 4, RB: 5, LT: 6, RT: 7, LS: 10, RS: 11 } as const;

export interface InputSettings {
  mouseSensitivity: number; // radians per pixel
  invertY: boolean;
  padLookSpeed: number; // radians per second at full deflection
  deadzone: number;
  responseExponent: number;
}

export const DEFAULT_INPUT_SETTINGS: InputSettings = {
  mouseSensitivity: 0.0022,
  invertY: false,
  padLookSpeed: 3.6,
  deadzone: 0.16,
  responseExponent: 1.6,
};

const STORAGE_KEY = 'battle.bindings.v1';

function loadBindings(): Record<Action, string[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_KEYS, ...(JSON.parse(raw) as Partial<Record<Action, string[]>>) };
  } catch {
    // Private mode or corrupt data: defaults.
  }
  return { ...DEFAULT_KEYS };
}

export function saveBindings(bindings: Partial<Record<Action, string[]>>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // Not persisted; still applies this session.
  }
}

export type Device = 'keyboard' | 'gamepad';

export interface InputManager {
  settings: InputSettings;
  readonly device: Device;
  readonly locked: boolean;
  /** Look deltas since the last call (radians), already scaled and inverted per settings. */
  takeLook(dt: number): { yaw: number; pitch: number };
  /** Build the sim input for this tick. */
  sample(cameraYaw: number, cameraPitch: number): Input;
  /** True once per press, for client-only toggles like lock-on. */
  consumePress(action: Action): boolean;
  /** Drop presses nobody consumed this frame, so they can't fire late. */
  endFrame(): void;
  dispose(): void;
}

export function createInput(target: HTMLElement): InputManager {
  const bindings = loadBindings();
  const down = new Set<string>();
  const pressed = new Set<string>();
  // Keys pressed since the last sim sample count as held for that sample, so a tap shorter
  // than a tick (16 ms) still reaches the sim.
  const latched = new Set<string>();
  let lookX = 0;
  let lookY = 0;
  let device: Device = 'keyboard';
  const settings = { ...DEFAULT_INPUT_SETTINGS };
  let padButtonsPrev: boolean[] = [];

  const press = (code: string) => {
    if (!down.has(code)) {
      pressed.add(code);
      latched.add(code);
    }
    down.add(code);
    device = 'keyboard';
  };
  const release = (code: string) => down.delete(code);

  const typing = (e: Event) =>
    e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
  const onKeyDown = (e: KeyboardEvent) => {
    if (typing(e)) return;
    if (e.code === 'Tab') e.preventDefault();
    if (e.repeat) return;
    press(e.code);
  };
  const onKeyUp = (e: KeyboardEvent) => release(e.code);
  const onMouseDown = (e: MouseEvent) => {
    if (document.pointerLockElement !== target) {
      target.requestPointerLock?.()?.catch?.(() => {});
      return;
    }
    press(`Mouse${e.button}`);
  };
  const onMouseUp = (e: MouseEvent) => release(`Mouse${e.button}`);
  const onMouseMove = (e: MouseEvent) => {
    if (document.pointerLockElement !== target) return;
    lookX += e.movementX;
    lookY += e.movementY;
    device = 'keyboard';
  };
  const onBlur = () => down.clear();
  const onContext = (e: Event) => e.preventDefault();

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  target.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('blur', onBlur);
  target.addEventListener('contextmenu', onContext);

  const held = (a: Action) => bindings[a].some((c) => down.has(c) || latched.has(c));

  function pad(): Gamepad | null {
    for (const p of navigator.getGamepads?.() ?? []) if (p?.connected) return p;
    return null;
  }

  function stick(x: number, y: number): [number, number] {
    const m = Math.hypot(x, y);
    if (m < settings.deadzone) return [0, 0];
    const k =
      ((Math.min(1, m) - settings.deadzone) / (1 - settings.deadzone)) **
        settings.responseExponent /
      m;
    return [x * k, y * k];
  }

  function padButton(p: Gamepad, i: number) {
    const b = p.buttons[i];
    return !!b && (b.pressed || b.value > 0.5);
  }

  function pollPad() {
    const p = pad();
    if (!p) return null;
    const buttons = p.buttons.map((_, i) => padButton(p, i));
    const edges = buttons.map((b, i) => b && !padButtonsPrev[i]);
    padButtonsPrev = buttons;
    const [mx, my] = stick(p.axes[0] ?? 0, p.axes[1] ?? 0);
    const [lx, ly] = stick(p.axes[2] ?? 0, p.axes[3] ?? 0);
    if (mx || my || lx || ly || buttons.some(Boolean)) device = 'gamepad';
    return { p, buttons, edges, mx, my, lx, ly };
  }

  let padState = pollPad();

  return {
    settings,
    get device() {
      return device;
    },
    get locked() {
      return document.pointerLockElement === target;
    },
    takeLook(dt) {
      padState = pollPad();
      let yaw = -lookX * settings.mouseSensitivity;
      let pitch = -lookY * settings.mouseSensitivity;
      lookX = lookY = 0;
      if (padState) {
        yaw -= padState.lx * settings.padLookSpeed * dt;
        pitch -= padState.ly * settings.padLookSpeed * 0.7 * dt;
      }
      if (settings.invertY) pitch = -pitch;
      return { yaw, pitch };
    },
    sample(cameraYaw, cameraPitch) {
      let x = (held('right') ? 1 : 0) - (held('left') ? 1 : 0);
      let y = (held('forward') ? 1 : 0) - (held('back') ? 1 : 0);
      const len = Math.hypot(x, y);
      if (len > 1) {
        x /= len;
        y /= len;
      }
      let buttons = 0;
      if (held('jump')) buttons |= Button.Jump;
      if (held('dodge')) buttons |= Button.Dodge;
      if (held('brace')) buttons |= Button.Brace;
      if (held('attack')) buttons |= Button.Attack;
      if (held('aim')) buttons |= Button.Aim;
      if (padState) {
        const { buttons: b, mx, my } = padState;
        if (Math.hypot(mx, my) > Math.hypot(x, y)) {
          x = mx;
          y = -my;
        }
        if (b[PAD.A]) buttons |= Button.Jump;
        if (b[PAD.B]) buttons |= Button.Dodge;
        if (b[PAD.LB]) buttons |= Button.Brace;
        if (b[PAD.RT]) buttons |= Button.Attack;
        if (b[PAD.LT]) buttons |= Button.Aim;
      }
      latched.clear();
      return {
        moveX: quantizeStick(x),
        moveY: quantizeStick(y),
        yaw: quantizeYaw(cameraYaw),
        pitch: quantizePitch(cameraPitch),
        buttons,
      };
    },
    consumePress(action) {
      const key = bindings[action].find((c) => pressed.has(c));
      if (key) {
        for (const c of bindings[action]) pressed.delete(c);
        return true;
      }
      if (action === 'lockOn' && padState?.edges[PAD.RS]) {
        padState.edges[PAD.RS] = false;
        return true;
      }
      return false;
    },
    endFrame() {
      pressed.clear();
    },
    dispose() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      target.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('blur', onBlur);
      target.removeEventListener('contextmenu', onContext);
    },
  };
}
